
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class ReportingService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) { }

  getModuleAssignmentReport(
    accountUuid: string,
    fetchLiveReport: boolean,
    fetchSummary: boolean,
    moduleAssignmentType: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');

    return this.http
      .post<any>(
        `${this.backendUrl}/report/moduleAssignment/findModuleAssignmentsByAccountUuid`,
        {
          accountUuid: accountUuid,
          fetchLiveReport: fetchLiveReport,
          fetchSummary: fetchSummary,
          moduleAssignmentType: moduleAssignmentType,
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`moduleAssignmentReport: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `moduleAssignmentReport request`
          )
        )
      );
  }

  getLearnersList(
    accountUuid: string,
    moduleAssignmentUuid: string,
    category: string,
    moduleAssignmentType: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');

    return this.http
      .post<any>(`${this.backendUrl}/report/moduleAssignment/getLearnersList`, {
        accountUuid: accountUuid,
        moduleAssignmentUuid: moduleAssignmentUuid,
        category: category,
        moduleAssignmentType: moduleAssignmentType,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getLearnersList: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getLearnersList`)
        )
      );
  }

  getLearnersActivity(
    accountUuid: string,
    moduleAssignmentUuid: string,
    category: string,
    moduleAssignmentType: string
  ): Observable<Blob> {
    if (!accountUuid) throw new Error('accountUuid is null');
    const options = {
      responseType: 'blob' as 'json',
    };

    return this.http
      .post<Blob>(
        `${this.backendUrl}/report/moduleAssignment/getLearnersActivity`,
        {
          accountUuid: accountUuid,
          moduleAssignmentUuid: moduleAssignmentUuid,
          category: category,
          moduleAssignmentType: moduleAssignmentType,
        },
        options
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<Blob>(
            `getLearnersActivity`
          )(error);
        })
      );
  }

  getModuleDetails(accountUuid: string, moduleUuid: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/findModuleDetails?moduleUuid=${moduleUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModuleDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getModuleDetails`)
        )
      );
  }

  getReportGroups(accountUuid: string, moduleUuid: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/findReportGroups?moduleUuid=${moduleUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getReportGroups: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getReportGroups`)
        )
      );
  }

  getPrecalcReportGroupChildData(
    accountUuid: string,
    moduleUuid: string,
    reportGroupUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/precalcReportGroupData?accountUuid=${accountUuid}&moduleUuid=${moduleUuid}&reportGroupUuid=${reportGroupUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPrecalcReportGroupChildData: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getPrecalcReportGroupChildData`
          )
        )
      );
  }

  getModulesList(
    accountUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');

    return this.http
      .get<any>(
        `${this.backendUrl}/report/moduleAssignment/findModulesByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModulesList: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getModulesList request`)
        )
      );
  }

  getCallsWithOverviewByModule(accountUuid: string, moduleUuid: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/findCallsWithOverviewByModuleUuid?accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCallsWithOverviewByModule: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getCallsWithOverviewByModule request`
          )
        )
      );
  }

  getCallDetails(accountUuid: string, callUuid: string, moduleUuid: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getGuidedCallOverallStats?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCallsWithOverviewByModule: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getCallsWithOverviewByModule request`
          )
        )
      );
  }
  getModuleQuestionAndFactSummary(accountUuid: string, moduleUuid: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/findModuleQuestionAndFactSummary?accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCallDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`getCallDetails request`)
        )
      );
  }

  getModuleSummaryCards(accountUuid: string, moduleUuid: string, callType: string): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/fetchModuleSummaryCards?accountUuid=${accountUuid}&moduleUuid=${moduleUuid}&callType=${callType}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`fetchModuleSummaryCards: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(`fetchModuleSummaryCards request`)
        )
      );
  }

  getCallQuestionFactDetail(
    accountUuid: string,
    callUuid: string,
    questionUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getCallQuestionFactDetail?callUuid=${callUuid}&questionUuid=${questionUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getCallQuestionFactDetail: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getCallQuestionFactDetail request`
          )
        )
      );
  }

  getConversationalCallDetail(
    accountUuid: string,
    callUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getConversationalCallDetail?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getConversationalCallDetail: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getConversationalCallDetail request`
          )
        )
      );
  }

  getConversationalCallObjectivesStats(
    accountUuid: string,
    callUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getConversationalCallObjectivesStats?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getConversationalCallObjectivesStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getConversationalCallObjectivesStats request`
          )
        )
      );
  }

  getConversationalCallMessageAdherenceStats(
    accountUuid: string,
    callUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getConversationalCallMessageAdherenceStats?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getConversationalCallMessageAdherenceStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getConversationalCallMessageAdherenceStats request`
          )
        )
      );
  }

  getConversationalCallSkillsStats(
    accountUuid: string,
    callUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getConversationalCallSkillsStats?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getConversationalCallSkillsStats: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getConversationalCallSkillsStats request`
          )
        )
      );
  }

  getObjectiveDetails(
    accountUuid: string,
    moduleUuid: string,
    title: string,
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');

    return this.http
      .post<any>(
        `${this.backendUrl}/report/module/fetchObjectiveDetails`,
        {
          accountUuid: accountUuid,
          moduleUuid: moduleUuid,
          title: title
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getObjectiveDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getObjectiveDetails request`
          )
        )
      );
  }

  fetchMessageAdherenceDetails(
    accountUuid: string,
    moduleUuid: string,
    title: string,
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');

    return this.http
      .post<any>(
        `${this.backendUrl}/report/module/fetchMessageAdherenceDetails`,
        {
          accountUuid: accountUuid,
          moduleUuid: moduleUuid,
          title: title
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`fetchMessageAdherenceDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `fetchMessageAdherenceDetails request`
          )
        )
      );
  }

  getModuleAssignmentSummary(
    accountUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/moduleAssignment/findModuleAssignmentSummaryByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModuleAssignmentSummary: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getModuleAssignmentSummary request`
          )
        )
      );
  }

  getPrecalcPracticeReportGroupChildData(
    accountUuid: string,
    moduleUuid: string,
    reportGroupUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/report/module/preCalcPracticeModuleReportGroupData?accountUuid=${accountUuid}&moduleUuid=${moduleUuid}&reportGroupUuid=${reportGroupUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPrecalcPracticeReportGroupChildData: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getPrecalcPracticeReportGroupChildData`
          )
        )
      );
  }

  getConversationalCallMessageAdherenceV2Stats(
    accountUuid: string,
    callUuid: string,
    moduleUuid: string
  ): Observable<any> {
    if (!accountUuid) throw new Error('accountUuid is null');
    if (!callUuid) throw new Error('callUuid is null');
    if (!moduleUuid) throw new Error('moduleUuid is null');
    return this.http
      .get<any>(
        `${this.backendUrl}/call/getConversationalCallMessageAdherenceV2Stats?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(
            `getConversationalCallMessageAdherenceV2Stats: ${outcome}`
          );
        }),
        catchError(
          this.errorHandlingService.handleError<any>(
            `getConversationalCallMessageAdherenceV2Stats request`
          )
        )
      );
  }
}
